import React from 'react';
import { FaFileExcel, FaFilter } from 'react-icons/fa';
import CustomDatePicker from '../../../../../../components/common/CustomDatePicker/CustomDatePicker';
import { exportFilteredSalesData } from '../../utils/excelExport';
import { exportProductStatsToExcel } from '../../utils/productStatsExcelExport';
import { SalesStatistics } from '../../types';
import { DayOfWeek, ViewMode, DAYS_OF_WEEK, DAY_ABBREVIATIONS } from '../FrequencyStats/types';

type Section = 'general' | 'productos' | 'frecuencia';
type TimeRange = 'current' | '7d' | '30d' | 'custom';
type AggregationType = 'by_item' | 'by_user' | 'by_pedido_type' | 'by_menu_category';

interface CompactFilterBarProps {
  startDate: string;
  endDate: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  onShowMobileFilters?: () => void;
  section?: Section;
  timeRange?: TimeRange;
  aggregationType?: AggregationType;
  onTimeRangeChange?: (timeRange: TimeRange) => void;
  onAggregationTypeChange?: (aggregationType: AggregationType) => void;
  salesData?: SalesStatistics[];
  selectedDay?: DayOfWeek;
  viewMode?: ViewMode;
  onSelectedDayChange?: (day: DayOfWeek) => void;
  onViewModeChange?: (mode: ViewMode) => void;
}

type TimePeriod = '1S' | '1M' | '6M' | '1A';

const TIME_RANGE_LABELS: Record<TimeRange, string> = {
  current: 'Última sesión', '7d': '7 días', '30d': '30 días', custom: 'Personalizado',
};
const AGG_LABELS: Record<AggregationType, string> = {
  by_item: 'Por producto', by_menu_category: 'Por categoría',
  by_user: 'Por usuario', by_pedido_type: 'Por canal',
};
const VIEW_MODE_LABELS: Record<ViewMode, string> = {
  both: 'Ambos', frequency: 'Frecuencia', amount: 'Ingresos',
};

const CompactFilterBar: React.FC<CompactFilterBarProps> = ({
  startDate, endDate, onStartDateChange, onEndDateChange, onShowMobileFilters,
  section = 'general', timeRange = 'current', aggregationType = 'by_item',
  onTimeRangeChange, onAggregationTypeChange, salesData = [],
  selectedDay = 'Monday', viewMode = 'both', onSelectedDayChange, onViewModeChange,
}) => {

  const handleTimePeriodClick = (period: TimePeriod) => {
    const today = new Date();
    const endDateStr = today.toISOString().split('T')[0];
    const d = new Date(today);
    switch (period) {
      case '1S': d.setDate(today.getDate() - 7); break;
      case '1M': d.setMonth(today.getMonth() - 1); break;
      case '6M': d.setMonth(today.getMonth() - 6); break;
      case '1A': d.setFullYear(today.getFullYear() - 1); break;
    }
    onStartDateChange(d.toISOString().split('T')[0]);
    onEndDateChange(endDateStr);
  };

  const getActivePeriod = (): TimePeriod | null => {
    const diffDays = Math.ceil(Math.abs(new Date().getTime() - new Date(startDate).getTime()) / 86400000);
    if (diffDays <= 8) return '1S';
    if (diffDays <= 32) return '1M';
    if (diffDays <= 185) return '6M';
    if (diffDays <= 370) return '1A';
    return null;
  };

  const activePeriod = getActivePeriod();

  const handleExportToExcel = () => {
    if (!salesData?.length) { alert('No hay datos para exportar'); return; }
    try { exportFilteredSalesData(salesData, startDate, endDate); }
    catch { alert('Error al exportar los datos. Por favor, intente nuevamente.'); }
  };

  const handleProductStatsExport = async () => {
    try {
      const localId = parseInt(localStorage.getItem('local_id') || '1');
      await exportProductStatsToExcel(aggregationType, timeRange, localId);
    } catch { alert('Error al exportar a Excel. Por favor, intenta nuevamente.'); }
  };

  const handleDateChange = (setter: (d: string) => void) => (date: string) => {
    setter(date);
    if (section === 'productos' && timeRange !== 'custom') onTimeRangeChange?.('custom');
  };

  return (
    <div className="mp-toolbar flex items-center gap-2 flex-wrap">

      {/* Productos: Time range toggle */}
      {section === 'productos' && (
        <div className="mp-toggle-group">
          {(['current', '7d', '30d'] as TimeRange[]).map((range) => (
            <button key={range} className={`mp-toggle-btn ${timeRange === range ? 'mp-toggle-active' : ''}`}
              onClick={() => onTimeRangeChange?.(range)}>
              {TIME_RANGE_LABELS[range]}
            </button>
          ))}
        </div>
      )}

      {/* Productos: Aggregation type toggle */}
      {section === 'productos' && (
        <div className="mp-toggle-group">
          {(['by_item', 'by_menu_category', 'by_user', 'by_pedido_type'] as AggregationType[]).map((type) => (
            <button key={type} className={`mp-toggle-btn ${aggregationType === type ? 'mp-toggle-active' : ''}`}
              onClick={() => onAggregationTypeChange?.(type)}>
              {AGG_LABELS[type]}
            </button>
          ))}
        </div>
      )}

      {/* Frecuencia: Day selector (L M X J V S D) */}
      {section === 'frecuencia' && (
        <div className="mp-toggle-group mp-toggle-group-compact">
          {DAYS_OF_WEEK.map((day: DayOfWeek) => (
            <button key={day} className={`mp-toggle-btn ${selectedDay === day ? 'mp-toggle-active' : ''}`}
              onClick={() => onSelectedDayChange?.(day)}>
              {DAY_ABBREVIATIONS[day]}
            </button>
          ))}
        </div>
      )}

      {/* Frecuencia: View mode toggle */}
      {section === 'frecuencia' && (
        <div className="mp-toggle-group">
          {(['both', 'frequency', 'amount'] as ViewMode[]).map((mode) => (
            <button key={mode} className={`mp-toggle-btn ${viewMode === mode ? 'mp-toggle-active' : ''}`}
              onClick={() => onViewModeChange?.(mode)}>
              {VIEW_MODE_LABELS[mode]}
            </button>
          ))}
        </div>
      )}

      {/* General: Time period toggle (1S 1M 6M 1A) */}
      {section === 'general' && (
        <div className="mp-toggle-group">
          {(['1S', '1M', '6M', '1A'] as TimePeriod[]).map((period) => (
            <button key={period} className={`mp-toggle-btn ${activePeriod === period ? 'mp-toggle-active' : ''}`}
              onClick={() => handleTimePeriodClick(period)}>
              {period}
            </button>
          ))}
        </div>
      )}

      {/* Date pickers */}
      <CustomDatePicker value={startDate} onChange={handleDateChange(onStartDateChange)} placeholder="Desde" />
      <CustomDatePicker value={endDate} onChange={handleDateChange(onEndDateChange)} placeholder="Hasta" />

      {/* Excel export — General */}
      {section === 'general' && (
        <button className="mp-btn-export" onClick={handleExportToExcel} title="Exportar a Excel">
          <FaFileExcel /> <span>Excel</span>
        </button>
      )}

      {/* Excel export — Productos */}
      {section === 'productos' && (
        <button className="mp-btn-export" onClick={handleProductStatsExport} title="Exportar estadísticas a Excel">
          <FaFileExcel /> <span>Excel</span>
        </button>
      )}

      {/* Mobile filter button */}
      <button className="mp-btn-icon ml-auto lg:hidden" onClick={onShowMobileFilters}>
        <FaFilter />
      </button>
    </div>
  );
};

export default CompactFilterBar;
